/**
 * RFP Upload Modal
 * Upload RFP/RFSO documents for template generation
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Card, Badge, getEntityCountsBatch, getArtifactChunks, listArtifacts } from '@tracekit/core';
import { Upload, FileText, X, CheckCircle, AlertCircle, Loader2, Search, Brain, Zap, StopCircle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';
import { useRFPJobStream } from '../../hooks/useRFPJobStream';

interface RFPUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunityId: string;
  projectId: string;
  onUploadComplete: (artifactIds: string[], jobId: string) => void;
  onCachedTemplateLoad?: (template: any) => void;  // NEW: Callback for cached template
  isGeneratingTemplate?: boolean;
  templateStream?: {
    isStreaming: boolean;
    stage: string;
    message: string;
    elapsed?: number;
    progress?: number;
  };
  disableCacheCheck?: boolean;  // NEW: Disable cache check (for ProposalBuilder flow)
}

interface UploadedDocument {
  artifact_id: string;
  filename: string;
  file_size: number;
  uploaded_at: string;
  processing_status: string;
  chunks_count?: number;
  sections_extracted?: number;
  entities_count?: number;
}

export function RFPUploadModal({
  isOpen,
  onClose,
  opportunityId,
  projectId,
  onUploadComplete,
  onCachedTemplateLoad,
  isGeneratingTemplate: externalIsGeneratingTemplate = false,
  templateStream,
  disableCacheCheck = false,
}: RFPUploadModalProps) {
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const isGenerating = externalIsGeneratingTemplate || isGeneratingTemplate || (templateStream?.isStreaming ?? false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadedJobId, setUploadedJobId] = useState<string | null>(null);
  const [uploadedArtifactIds, setUploadedArtifactIds] = useState<string[]>([]);
  const [uploadedDocumentsInfo, setUploadedDocumentsInfo] = useState<UploadedDocument[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [allArtifactsForTemplate, setAllArtifactsForTemplate] = useState<UploadedDocument[]>([]);
  const [selectedExistingDocs, setSelectedExistingDocs] = useState<string[]>([]); // NEW: Track selected existing docs
  const queryClient = useQueryClient();
  
  // RFP job streaming for monitoring processing
  const rfpJobStream = useRFPJobStream();
  
  // Check for cached template (disabled when used from ProposalBuilder)
  const { data: cachedTemplate, refetch: refetchCachedTemplate } = useQuery({
    queryKey: ['cached-template', opportunityId],
    queryFn: async () => {
      try {
        const api = getAPIClient();
        const url = `/api/opportunities/${opportunityId}/proposal-template/cache`;
        const data = await api.get(url, {
          params: { project_id: projectId },
        });
        return data;
      } catch (error: any) {
        // No cache or error - return null
        console.log('[RFPUploadModal] No cached template found:', error.message);
        return null;
      }
    },
    enabled: isOpen && !!opportunityId && !!projectId && !disableCacheCheck,
    staleTime: 0,
    refetchOnMount: true,
  });
  
  // Fetch existing RFP documents - MUST be defined before useEffects that use refetchDocuments
  const { data: existingDocuments, refetch: refetchDocuments, isLoading: isLoadingDocs } = useQuery({
    queryKey: ['rfp-documents', opportunityId, projectId],
    queryFn: async () => {
      console.log('[RFPUploadModal] ===== STARTING API FETCH =====');
      console.log('[RFPUploadModal] Params:', { opportunityId, projectId, isOpen });
      
      let api;
      try {
        console.log('[RFPUploadModal] Step 1: Getting API client...');
        api = getAPIClient();
        console.log('[RFPUploadModal] Step 2: API client obtained successfully');
      } catch (clientError) {
        console.error('[RFPUploadModal] FATAL: Failed to get API client:', clientError);
        console.error('[RFPUploadModal] Client error details:', {
          name: (clientError as Error).name,
          message: (clientError as Error).message,
          stack: (clientError as Error).stack
        });
        return { documents: [], total: 0 } as { documents: UploadedDocument[]; total: number };
      }
      
      try {
        console.log('[RFPUploadModal] Step 3: Making API request...');
        const url = `/api/opportunities/${opportunityId}/rfp-documents`;
        
        // APIClient.get() returns DATA directly (unwrapped), not axios response!
        const data = await api.get(url, {
          params: { project_id: projectId },
        });
        
        console.log('[RFPUploadModal] Step 4: Data received!');
        console.log('[RFPUploadModal] FULL DATA:', data);
        console.log('[RFPUploadModal] Data type:', typeof data);
        console.log('[RFPUploadModal] Data keys:', data ? Object.keys(data) : 'null');
        console.log('[RFPUploadModal] Has documents:', !!data?.documents);
        console.log('[RFPUploadModal] Documents length:', data?.documents?.length);
        
        if (!data) {
          console.error('[RFPUploadModal] No data returned!');
          return { documents: [], total: 0 } as { documents: UploadedDocument[]; total: number };
        }
        
        // Extract documents array
        const documents = Array.isArray(data.documents) ? data.documents : [];
        const total = typeof data.total === 'number' ? data.total : documents.length;
        
        const result = {
          documents,
          total
        } as { documents: UploadedDocument[]; total: number };
        
        console.log('[RFPUploadModal] ✅ SUCCESS! Returning:', {
          documentsCount: result.documents.length,
          total: result.total,
          firstDoc: result.documents[0]
        });
        
        return result;
      } catch (error) {
        console.error('[RFPUploadModal] ===== API REQUEST FAILED =====');
        console.error('[RFPUploadModal] Error:', error);
        console.error('[RFPUploadModal] Error details:', {
          name: (error as Error).name,
          message: (error as Error).message,
          response: (error as any).response,
          responseData: (error as any).response?.data
        });
        return { documents: [], total: 0 } as { documents: UploadedDocument[]; total: number };
      }
    },
    enabled: isOpen && !!opportunityId && !!projectId,
    staleTime: 0,
    refetchOnMount: true,
  });
  
  // Debug log to check query state
  console.log('[RFPUploadModal] Query state:', {
    isOpen,
    opportunityId,
    projectId,
    enabled: isOpen && !!opportunityId && !!projectId,
    isLoadingDocs,
    hasData: !!existingDocuments,
    documentsCount: existingDocuments?.documents?.length || 0,
    showConfirmation
  });
  
  // When job completes, show confirmation step - DO NOT auto-trigger template generation
  // Template generation is ONLY triggered when user clicks "Generate Template" button
  useEffect(() => {
    if (rfpJobStream.stage === 'complete' && uploadedJobId && uploadedArtifactIds.length > 0 && !showConfirmation) {
      // Job completed - show confirmation step for user to review files
      // User must explicitly click "Generate Template" button to proceed
      console.log('[RFPUploadModal] Job completed, showing confirmation step - waiting for user to click Generate Template');
      setShowConfirmation(true);
      
      // Refresh document list and fetch chunks/entities counts for uploaded artifacts
      if (refetchDocuments && uploadedArtifactIds.length > 0 && projectId) {
        refetchDocuments().then(async (response) => {
          const allDocs = response.data?.documents || [];
          let uploadedDocs = allDocs.filter((doc: UploadedDocument) => 
            uploadedArtifactIds.includes(doc.artifact_id)
          );
          
          // Fetch chunks and entities counts for uploaded artifacts (similar to KnowledgeBase)
          try {
            // Fetch entity counts in batch (optimized)
            const entityCounts = await getEntityCountsBatch(projectId, uploadedArtifactIds);
            
            // Fetch chunk counts for each artifact
            const chunksPromises = uploadedArtifactIds.map(async (artifactId) => {
              try {
                const chunksResponse = await getArtifactChunks(artifactId);
                // API returns { total_chunks, chunks, pagination } - use total_chunks for accurate count
                const totalChunks = chunksResponse?.total_chunks ?? 0;
                return { artifactId, count: totalChunks };
              } catch (error) {
                console.error(`[RFPUploadModal] Failed to fetch chunks for artifact ${artifactId}:`, error);
                return { artifactId, count: 0 };
              }
            });
            
            const chunksResults = await Promise.all(chunksPromises);
            const chunksCounts: Record<string, number> = {};
            chunksResults.forEach(({ artifactId, count }) => {
              chunksCounts[artifactId] = count;
            });
            
            // Update uploaded docs with fetched counts
            uploadedDocs = uploadedDocs.map((doc) => ({
              ...doc,
              chunks_count: chunksCounts[doc.artifact_id] ?? doc.chunks_count,
              entities_count: entityCounts[doc.artifact_id] ?? doc.entities_count,
            }));
          } catch (error) {
            console.error('[RFPUploadModal] Failed to fetch artifact counts after job completion:', error);
            // Continue with existing data if fetch fails
          }
          
          setUploadedDocumentsInfo(uploadedDocs);
          
          // Combine newly uploaded with existing documents for template generation
          setAllArtifactsForTemplate([...uploadedDocs, ...allDocs.filter((doc: UploadedDocument) => 
            !uploadedArtifactIds.includes(doc.artifact_id) && doc.processing_status === 'completed'
          )]);
        });
      }
      
      // DO NOT call onUploadComplete here - only when user clicks button
    }
  }, [rfpJobStream.stage, uploadedJobId, uploadedArtifactIds, showConfirmation, refetchDocuments]);
  
  // Also update artifacts list when existing documents change (in case user opens modal with existing docs)
  // BUT: Don't interfere if we already have artifacts from upload
  useEffect(() => {
    if (existingDocuments && existingDocuments.documents.length > 0) {
      const allDocs = existingDocuments.documents;
      
      // Only update if we don't already have artifacts set from upload
      // This prevents clearing state after upload completes
      if (showConfirmation && (uploadedDocumentsInfo.length > 0 || allArtifactsForTemplate.length > 0)) {
        // We already have artifacts from upload - don't interfere
        console.log('[RFPUploadModal] Skipping existingDocuments update - already have artifacts from upload');
        return;
      }
      
      if (showConfirmation) {
        // In confirmation step: combine uploaded with existing
        const uploadedDocs = uploadedArtifactIds.length > 0 
          ? allDocs.filter((doc: UploadedDocument) => uploadedArtifactIds.includes(doc.artifact_id))
          : [];
        const existingDocs = allDocs.filter((doc: UploadedDocument) => 
          !uploadedArtifactIds.includes(doc.artifact_id) && doc.processing_status === 'completed'
        );
        if (uploadedDocs.length > 0 || existingDocs.length > 0) {
          setAllArtifactsForTemplate([...uploadedDocs, ...existingDocs]);
        }
      } else if (uploadedArtifactIds.length === 0 && allDocs.length > 0) {
        // If modal opens with existing docs but no new uploads, show them in confirmation
        const completedDocs = allDocs.filter((doc: UploadedDocument) => 
          doc.processing_status === 'completed'
        );
        if (completedDocs.length > 0) {
          setAllArtifactsForTemplate(completedDocs);
          setShowConfirmation(true);
        }
      }
    }
  }, [existingDocuments, uploadedArtifactIds, showConfirmation, uploadedDocumentsInfo.length, allArtifactsForTemplate.length]);
  
  // Reset state when modal closes or opens (ensure clean state on each open)
  useEffect(() => {
    if (!isOpen) {
      // Reset all state when modal closes
      setShowConfirmation(false);
      setUploadedDocumentsInfo([]);
      setAllArtifactsForTemplate([]);
      setUploadedArtifactIds([]);
      setUploadedJobId(null);
      setFiles([]);
      setIsGeneratingTemplate(false); // Reset local generating state
      setSelectedExistingDocs([]); // Reset selected existing docs
    } else {
      // When modal opens, ensure generating state is reset (in case it was left in a bad state)
      if (!externalIsGeneratingTemplate && !templateStream?.isStreaming) {
        setIsGeneratingTemplate(false);
      }
    }
  }, [isOpen, externalIsGeneratingTemplate, templateStream?.isStreaming]);
  
  // DEBUG: Log state changes
  useEffect(() => {
    console.log('[RFPUploadModal] State update:', {
      showConfirmation,
      uploadedDocumentsInfo: uploadedDocumentsInfo.length,
      allArtifactsForTemplate: allArtifactsForTemplate.length,
      uploadedArtifactIds: uploadedArtifactIds.length,
      artifacts: uploadedDocumentsInfo.length > 0 ? uploadedDocumentsInfo : allArtifactsForTemplate
    });
  }, [showConfirmation, uploadedDocumentsInfo, allArtifactsForTemplate, uploadedArtifactIds]);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (filesToUpload: File[]) => {
      const api = getAPIClient();
      const formData = new FormData();
      
      filesToUpload.forEach((file) => {
        formData.append('files', file);
      });

      // api.post already returns response.data, so we get the data object directly
      const responseData = await api.post(
        `/api/opportunities/${opportunityId}/rfp-documents/upload?project_id=${projectId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress((prev) => ({
                ...prev,
                [filesToUpload[0].name]: progress,
              }));
            }
          },
        }
      );

      console.log('[RFPUploadModal] API response data:', responseData);
      return responseData;
    },
    onSuccess: async (data) => {
      console.log('[RFPUploadModal] Upload success response:', data);
      setFiles([]);
      setUploadProgress({});
      
      // Validate response exists
      if (!data) {
        console.error('[RFPUploadModal] No response data received - attempting fallback lookup');
        // Try to get job ID from opportunity metadata as fallback
        try {
          const documentsResponse = await refetchDocuments();
          const documents = documentsResponse.data?.documents || [];
          
          if (documents.length > 0) {
            // Try to find job ID from metadata
            const api = getAPIClient();
            const oppResponse = await api.get(`/api/opportunities/${opportunityId}`, {
              params: { project_id: projectId },
            });
            
            const oppData = oppResponse?.data?.data || {};
            const rfpMetadata = oppData.rfp_document_metadata || {};
            
            // Find the most recent structure extraction job ID
            let foundJobId = '';
            for (const [artifactId, metadata] of Object.entries(rfpMetadata)) {
              if (metadata && typeof metadata === 'object') {
                // Check for structure job in opportunity metadata
                const structureJobId = (metadata as any).structure_job_id;
                if (structureJobId) {
                  foundJobId = structureJobId;
                  break;
                }
              }
            }
            
            if (foundJobId) {
              console.log('[RFPUploadModal] Found job ID from metadata:', foundJobId);
              const artifactIds = documents.map((d: UploadedDocument) => d.artifact_id);
              setUploadedJobId(foundJobId);
              rfpJobStream.startStream(opportunityId, foundJobId, projectId);
              onUploadComplete(artifactIds, foundJobId);
              return;
            }
          }
        } catch (fallbackError) {
          console.error('[RFPUploadModal] Fallback lookup failed:', fallbackError);
        }
        
        alert('Upload completed but no response data was received. The file may still be processing. Please check the documents list.');
        return;
      }
      
      // Handle both snake_case and camelCase response formats
      const artifactIds = data.artifact_ids || data.artifactIds || [];
      const jobId = data.job_id || data.jobId || '';
      const status = data.status || 'unknown';
      
      console.log('[RFPUploadModal] Parsed response:', { artifactIds, jobId, status, data });
      
      // Always validate artifact IDs first
      if (!artifactIds || !Array.isArray(artifactIds) || artifactIds.length === 0) {
        console.error('[RFPUploadModal] No artifact IDs in response:', data);
        alert('Upload completed but no artifact IDs were returned. Please check the console.');
        return;
      }
      
      // Always refetch documents to get latest status
      console.log('[RFPUploadModal] Refetching documents after upload...');
      const documentsResponse = await refetchDocuments();
      console.log('[RFPUploadModal] Documents refetch response:', {
        hasData: !!documentsResponse?.data,
        documentsCount: documentsResponse?.data?.documents?.length || 0,
        total: documentsResponse?.data?.total || 0,
        fullResponse: documentsResponse?.data
      });
      
      const allDocuments = documentsResponse?.data?.documents || [];
      console.log('[RFPUploadModal] All documents from refetch:', {
        count: allDocuments.length,
        artifactIds: allDocuments.map((d: UploadedDocument) => d.artifact_id)
      });
      
      // Get info for uploaded artifacts - try matching by artifact ID first
      let uploadedDocs = allDocuments.filter((doc: UploadedDocument) => 
        artifactIds.includes(doc.artifact_id)
      );
      console.log('[RFPUploadModal] Filtered uploaded docs by ID:', {
        count: uploadedDocs.length,
        artifactIds: uploadedDocs.map(d => d.artifact_id),
        filenames: uploadedDocs.map(d => d.filename)
      });
      
      // If we didn't find all artifacts in the documents list (e.g., duplicate/cached files),
      // query artifacts directly by ID to get their info
      if (uploadedDocs.length < artifactIds.length && projectId) {
        console.log('[RFPUploadModal] Some artifacts not found in documents list, querying artifacts directly...');
        try {
          // Get all artifacts for the project and match by ID
          const artifactsResponse = await listArtifacts(projectId, 1000, 0);
          const allArtifacts = artifactsResponse?.artifacts || [];
          console.log('[RFPUploadModal] All artifacts from listArtifacts:', {
            count: allArtifacts.length,
            artifactIds: allArtifacts.map(a => a.id)
          });
          
          // Find missing artifacts by ID
          const missingArtifactIds = artifactIds.filter(id => 
            !uploadedDocs.some(doc => doc.artifact_id === id)
          );
          console.log('[RFPUploadModal] Missing artifact IDs:', missingArtifactIds);
          
          for (const artifactId of missingArtifactIds) {
            const artifact = allArtifacts.find(a => a.id === artifactId);
            if (artifact) {
              console.log('[RFPUploadModal] Found missing artifact:', {
                id: artifact.id,
                filename: artifact.filename,
                sourceType: artifact.source_type
              });
              
              // Create UploadedDocument from Artifact
              const doc: UploadedDocument = {
                artifact_id: artifact.id,
                filename: artifact.filename || 'Unknown',
                file_size: artifact.file_size_bytes || 0,
                uploaded_at: artifact.created_at ? new Date(artifact.created_at).toISOString() : new Date().toISOString(),
                processing_status: 'completed', // Assume completed if it exists
                chunks_count: undefined, // Will be fetched below
                entities_count: undefined, // Will be fetched below
              };
              uploadedDocs.push(doc);
            } else {
              console.warn('[RFPUploadModal] Artifact not found even in full list:', artifactId);
            }
          }
          
          console.log('[RFPUploadModal] After querying artifacts directly:', {
            count: uploadedDocs.length,
            artifactIds: uploadedDocs.map(d => d.artifact_id),
            filenames: uploadedDocs.map(d => d.filename)
          });
        } catch (error) {
          console.error('[RFPUploadModal] Failed to query artifacts directly:', error);
        }
      }
      
      // Fetch chunks and entities counts for uploaded artifacts (similar to KnowledgeBase)
      if (artifactIds.length > 0 && projectId) {
        try {
          // Fetch entity counts in batch (optimized)
          const entityCounts = await getEntityCountsBatch(projectId, artifactIds);
          
          // Fetch chunk counts for each artifact
          const chunksPromises = artifactIds.map(async (artifactId) => {
            try {
              const chunksResponse = await getArtifactChunks(artifactId);
              // API returns { total_chunks, chunks, pagination } - use total_chunks for accurate count
              const totalChunks = (chunksResponse as any)?.total_chunks ?? (Array.isArray(chunksResponse) ? chunksResponse.length : 0);
              return { artifactId, count: totalChunks };
            } catch (error) {
              console.error(`[RFPUploadModal] Failed to fetch chunks for artifact ${artifactId}:`, error);
              return { artifactId, count: 0 };
            }
          });
          
          const chunksResults = await Promise.all(chunksPromises);
          const chunksCounts: Record<string, number> = {};
          chunksResults.forEach(({ artifactId, count }) => {
            chunksCounts[artifactId] = count;
          });
          
          // Update uploaded docs with fetched counts
          uploadedDocs = uploadedDocs.map((doc) => ({
            ...doc,
            chunks_count: chunksCounts[doc.artifact_id] ?? doc.chunks_count,
            entities_count: entityCounts[doc.artifact_id] ?? doc.entities_count,
          }));
        } catch (error) {
          console.error('[RFPUploadModal] Failed to fetch artifact counts:', error);
          // Continue with existing data if fetch fails
        }
      }
      
      // CRITICAL: Set artifact IDs FIRST before any other state
      // This ensures useEffect hooks don't interfere
      console.log('[RFPUploadModal] Setting state - artifactIds:', artifactIds);
      setUploadedArtifactIds(artifactIds);
      
      // IMPORTANT: Set state immediately so UI can show artifacts
      // Even if uploadedDocs is empty initially, set it so UI can update
      console.log('[RFPUploadModal] Setting uploadedDocumentsInfo:', {
        count: uploadedDocs.length,
        docs: uploadedDocs.map(d => ({ id: d.artifact_id, filename: d.filename, chunks: d.chunks_count, entities: d.entities_count }))
      });
      setUploadedDocumentsInfo(uploadedDocs.length > 0 ? uploadedDocs : []);
      
      // Combine newly uploaded with existing documents for template generation
      // This ensures we show ALL artifacts that will be used for template generation
      const existingCompletedDocs = allDocuments.filter((doc: UploadedDocument) => 
        !artifactIds.includes(doc.artifact_id) && doc.processing_status === 'completed'
      );
      const allArtifacts = [...uploadedDocs, ...existingCompletedDocs];
      console.log('[RFPUploadModal] Setting allArtifactsForTemplate:', {
        uploaded: uploadedDocs.length,
        existing: existingCompletedDocs.length,
        total: allArtifacts.length,
        allArtifacts: allArtifacts.map(d => ({ id: d.artifact_id, filename: d.filename }))
      });
      setAllArtifactsForTemplate(allArtifacts.length > 0 ? allArtifacts : []);
      
      // CRITICAL: Show confirmation immediately so UI displays artifacts
      // This MUST be set regardless of whether we have artifacts yet
      // Set this LAST to ensure all other state is set first
      console.log('[RFPUploadModal] Setting showConfirmation = true');
      setShowConfirmation(true);
      
      console.log('[RFPUploadModal] Artifacts for template:', {
        uploaded: uploadedDocs.length,
        existing: existingCompletedDocs.length,
        total: allArtifacts.length,
        artifactIds,
        showConfirmation: true,
        uploadedDocs: uploadedDocs.map(d => ({ id: d.artifact_id, filename: d.filename, chunks: d.chunks_count, entities: d.entities_count })),
        allArtifacts: allArtifacts.map(d => ({ id: d.artifact_id, filename: d.filename, chunks: d.chunks_count, entities: d.entities_count }))
      });
      
      // If we have a job ID, start monitoring immediately
      if (jobId && jobId.trim() !== '') {
        console.log('[RFPUploadModal] Starting job monitoring for job:', jobId);
        setUploadedJobId(jobId);
        rfpJobStream.startStream(opportunityId, jobId, projectId);
      } else {
        // If no job ID (cached file, already processed), show confirmation immediately
        console.log('[RFPUploadModal] No job ID - file may be cached/already processed, showing confirmation');
        // Still fetch counts if we haven't already
        if (allArtifacts.length > 0 && projectId) {
          // Ensure we have counts for all artifacts
          const allArtifactIds = allArtifacts.map(doc => doc.artifact_id);
          try {
            const entityCounts = await getEntityCountsBatch(projectId, allArtifactIds);
            const chunksPromises = allArtifactIds.map(async (artifactId) => {
              try {
                const chunksResponse = await getArtifactChunks(artifactId);
                // API returns { total_chunks, chunks, pagination } - use total_chunks for accurate count
                const totalChunks = chunksResponse?.total_chunks ?? 0;
                return { artifactId, count: totalChunks };
              } catch (error) {
                return { artifactId, count: 0 };
              }
            });
            const chunksResults = await Promise.all(chunksPromises);
            const chunksCounts: Record<string, number> = {};
            chunksResults.forEach(({ artifactId, count }) => {
              chunksCounts[artifactId] = count;
            });
            
            // Update all artifacts with counts
            const updatedArtifacts = allArtifacts.map((doc) => ({
              ...doc,
              chunks_count: chunksCounts[doc.artifact_id] ?? doc.chunks_count ?? 0,
              entities_count: entityCounts[doc.artifact_id] ?? doc.entities_count ?? 0,
            }));
            
            // CRITICAL: Update state atomically to prevent flickering
            setAllArtifactsForTemplate(updatedArtifacts);
            setUploadedDocumentsInfo(updatedArtifacts.filter(doc => artifactIds.includes(doc.artifact_id)));
            // Ensure confirmation stays true
            setShowConfirmation(true);
            
            console.log('[RFPUploadModal] Updated artifacts with counts:', {
              total: updatedArtifacts.length,
              uploaded: updatedArtifacts.filter(doc => artifactIds.includes(doc.artifact_id)).length,
              showConfirmation: true
            });
          } catch (error) {
            console.error('[RFPUploadModal] Failed to fetch counts for cached file:', error);
            // Still show confirmation even if counts fail - preserve existing state
            setShowConfirmation(true);
          }
        } else {
          // No artifacts to fetch, but still show confirmation
          setShowConfirmation(true);
        }
      }
      
      // Show confirmation step - user can review files before template generation
      // This happens regardless of job ID (files may already be processed)
      // Note: We already set this above, but ensure it's set for jobs too
      if (jobId && jobId.trim() !== '') {
        // For jobs, confirmation is already set above
        // The useEffect will handle updates when job completes
        console.log('[RFPUploadModal] Job ID present, confirmation already set');
      }
    },
    onError: (error: any) => {
      console.error('[RFPUploadModal] Upload failed:', error);
      console.error('[RFPUploadModal] Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
      alert(`Upload failed: ${errorMessage}`);
    },
    onSettled: () => {
      setUploading(false);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (files.length === 0) {
      alert('Please select at least one file');
      return;
    }

    setUploading(true);
    uploadMutation.mutate(files);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (file) => file.type === 'application/pdf' || file.name.endsWith('.docx') || file.name.endsWith('.doc')
    );
    setFiles((prev) => [...prev, ...droppedFiles]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upload RFP Documents"
      size="xl"
    >
      <div className="space-y-6">
        {/* DEBUG: Log render state */}
        {(() => {
          console.log('[RFPUploadModal RENDER] Current state:', {
            isOpen,
            isLoadingDocs,
            showConfirmation,
            uploadedDocumentsInfo: uploadedDocumentsInfo.length,
            allArtifactsForTemplate: allArtifactsForTemplate.length,
            uploadedArtifactIds: uploadedArtifactIds.length,
            shouldShowArtifacts: showConfirmation && (uploadedDocumentsInfo.length > 0 || allArtifactsForTemplate.length > 0),
            existingDocuments: existingDocuments ? {
              total: existingDocuments.total,
              documentsLength: existingDocuments.documents?.length || 0
            } : null,
            uploadedDocs: uploadedDocumentsInfo.map(d => ({ id: d.artifact_id, filename: d.filename })),
            allArtifacts: allArtifactsForTemplate.map(d => ({ id: d.artifact_id, filename: d.filename }))
          });
          return null;
        })()}
        
        {/* Show uploaded files info OR upload area */}
        {showConfirmation && (uploadedDocumentsInfo.length > 0 || allArtifactsForTemplate.length > 0) ? (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Documents Ready for Template Generation ({(allArtifactsForTemplate.length > 0 ? allArtifactsForTemplate : uploadedDocumentsInfo).length})
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(allArtifactsForTemplate.length > 0 ? allArtifactsForTemplate : uploadedDocumentsInfo).map((doc) => (
                <div
                  key={doc.artifact_id}
                  className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-theme-text truncate">
                        {doc.filename}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        {doc.chunks_count !== undefined && (
                          <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                            <span className="font-semibold">{doc.chunks_count}</span>
                            <span>chunks</span>
                          </span>
                        )}
                        {doc.entities_count !== undefined && (
                          <span className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                            <span className="font-semibold">{doc.entities_count}</span>
                            <span>entities</span>
                          </span>
                        )}
                        {doc.chunks_count === undefined && doc.entities_count === undefined && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                            Processing...
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Badge
                    variant={doc.processing_status === 'completed' ? 'success' : 'default'}
                    className="text-xs ml-2 flex-shrink-0"
                  >
                    {doc.processing_status}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-400 dark:hover:border-blue-600 transition-colors"
          >
            <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-sm text-gray-600 dark:text-theme-text-secondary mb-4">
              Drag and drop RFP/RFSO documents here, or click to browse
            </p>
            <input
              type="file"
              accept=".pdf,.docx,.doc"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="rfp-file-input"
            />
            <label
              htmlFor="rfp-file-input"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors"
            >
              Select Files
            </label>
            <p className="text-xs text-gray-500 dark:text-theme-text-secondary mt-2">
              Supported: PDF, DOCX (max 50MB per file)
            </p>
          </div>
        )}

        {/* Selected Files */}
        {files.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-theme-text">
              Selected Files ({files.length})
            </h3>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-theme-text truncate">
                      {file.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  {uploadProgress[file.name] !== undefined ? (
                    <div className="text-xs text-blue-600">
                      {uploadProgress[file.name]}%
                    </div>
                  ) : (
                    <button
                      onClick={() => handleRemoveFile(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Previously Uploaded Documents (Enhanced with Selection) */}
        {existingDocuments && existingDocuments.documents && existingDocuments.documents.length > 0 && (
          <div className="space-y-2 mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-gray-700 dark:text-theme-text">
                  Previously Uploaded RFP Documents ({existingDocuments.total})
                </h3>
                <Badge variant="default" className="text-xs">
                  {existingDocuments.documents.filter(d => d.processing_status === 'completed').length} processed
                </Badge>
              </div>
              {existingDocuments.documents.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedExistingDocs(existingDocuments.documents.map(d => d.artifact_id))}
                    className="text-xs"
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedExistingDocs([])}
                    className="text-xs"
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Select documents to include in template generation
            </p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {existingDocuments.documents.map((doc) => {
                const isSelected = selectedExistingDocs.includes(doc.artifact_id);
                
                return (
                  <Card 
                    key={doc.artifact_id} 
                    className={`p-3 cursor-pointer transition-all ${
                      isSelected 
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700' 
                        : 'bg-white dark:bg-gray-800/30 border-gray-200 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-800'
                    }`}
                    onClick={(e) => {
                      // Allow selection of all documents (pending or completed)
                      // Don't toggle if clicking the checkbox directly (it has its own handler)
                      if ((e.target as HTMLElement).tagName === 'INPUT') return;
                      
                      if (isSelected) {
                        setSelectedExistingDocs(selectedExistingDocs.filter(id => id !== doc.artifact_id));
                      } else {
                        setSelectedExistingDocs([...selectedExistingDocs, doc.artifact_id]);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation(); // Prevent card onClick from firing
                          if (isSelected) {
                            setSelectedExistingDocs(selectedExistingDocs.filter(id => id !== doc.artifact_id));
                          } else {
                            setSelectedExistingDocs([...selectedExistingDocs, doc.artifact_id]);
                          }
                        }}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                      <FileText className={`h-4 w-4 flex-shrink-0 ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-theme-text truncate" title={doc.filename}>
                          {doc.filename}
                        </p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge
                            variant={doc.processing_status === 'completed' ? 'success' : doc.processing_status === 'processing' ? 'warning' : 'default'}
                            className="text-xs"
                          >
                            {doc.processing_status}
                          </Badge>
                          {doc.file_size && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatFileSize(doc.file_size)}
                            </span>
                          )}
                          {doc.chunks_count !== undefined && (
                            <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                              {doc.chunks_count} chunks
                            </span>
                          )}
                          {doc.entities_count !== undefined && (
                            <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                              {doc.entities_count} entities
                            </span>
                          )}
                          {doc.sections_extracted !== undefined && (
                            <span className="text-xs text-purple-600 dark:text-purple-400">
                              {doc.sections_extracted} sections
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
            {/* Action buttons for existing documents */}
            <div className="mt-3 space-y-2">
              {/* Cached template available */}
              {cachedTemplate && (
                <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <div>
                      <span className="text-sm font-medium text-gray-700 dark:text-theme-text">
                        Template cached
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Generated {new Date(cachedTemplate.generated_at).toLocaleDateString()} • 
                        {' '}{cachedTemplate.questions_count} questions, {cachedTemplate.sections_count} sections
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={async () => {
                      console.log('[RFPUploadModal] Loading cached template:', cachedTemplate);
                      
                      // FIXED: Actually load and use the cached template data
                      if (cachedTemplate) {
                        try {
                          // Fetch the full cached template from backend
                          const api = getAPIClient();
                          const fullTemplate = await api.get(
                            `/api/opportunities/${opportunityId}/proposal-template/cache`,
                            { params: { project_id: projectId } }
                          );
                          
                          console.log('[RFPUploadModal] Loaded cached template:', {
                            sections: fullTemplate.sections?.length || 0,
                            questions: fullTemplate.default_questions?.length || 0,
                            source: fullTemplate.template_source
                          });
                          
                          // Use the new callback to pass template to parent
                          if (onCachedTemplateLoad) {
                            onCachedTemplateLoad(fullTemplate);
                          }
                          
                          onClose();
                        } catch (error) {
                          console.error('[RFPUploadModal] Failed to load cached template:', error);
                          alert('Failed to load cached template. Please try regenerating.');
                        }
                      }
                    }}
                  >
                    <Zap className="h-4 w-4 mr-1" />
                    Load Cached Template
                  </Button>
                </div>
              )}
              
              {/* Selected documents action */}
              {selectedExistingDocs.length > 0 && (
                <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                  <span className="text-sm text-gray-700 dark:text-theme-text">
                    <strong>{selectedExistingDocs.length}</strong> document{selectedExistingDocs.length !== 1 ? 's' : ''} selected
                  </span>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      // Generate template with selected existing documents only
                      const selectedDocs = existingDocuments.documents.filter(d => selectedExistingDocs.includes(d.artifact_id));
                      setUploadedDocumentsInfo(selectedDocs);
                      setAllArtifactsForTemplate(selectedDocs);
                      setShowConfirmation(true);
                    }}
                    disabled={selectedExistingDocs.length === 0}
                  >
                    Generate New Template ({selectedExistingDocs.length})
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Template Generation Progress - ENHANCED FOR PHASE 2 */}
        {isGenerating && templateStream && (
          <div className="p-4 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 dark:from-purple-900/30 dark:via-indigo-900/30 dark:to-purple-900/30 border-2 border-purple-300 dark:border-purple-700 rounded-lg shadow-lg">
            {/* Task Info Header */}
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-purple-200 dark:border-purple-700">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
                  Phase 2: Template & Question Generation
                </span>
              </div>
              {templateStream.elapsed !== undefined && (
                <div className="text-xs text-purple-600 dark:text-purple-400 font-mono">
                  {templateStream.elapsed.toFixed(1)}s elapsed
                </div>
              )}
            </div>

            {/* Main Status */}
            <div className="flex items-center gap-3 mb-3">
              <div className="relative">
                {templateStream.stage === 'started' || templateStream.stage === 'task_started' ? (
                  <Loader2 className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-spin" />
                ) : templateStream.stage === 'template_generation' || templateStream.stage === 'generating' ? (
                  <Brain className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-pulse" />
                ) : templateStream.stage === 'retrieval' ? (
                  <Search className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-pulse" />
                ) : templateStream.stage === 'parsing' || templateStream.stage === 'structure_extraction' ? (
                  <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-bounce" />
                ) : templateStream.stage === 'complete' ? (
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                ) : (
                  <Loader2 className="h-5 w-5 text-purple-600 dark:text-purple-400 animate-spin" />
                )}
                {templateStream.stage !== 'complete' && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-500"></span>
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-purple-900 dark:text-purple-200 mb-1">
                  {templateStream.message || 'Generating comprehensive template...'}
                </div>
                <div className="text-xs text-purple-700 dark:text-purple-300">
                  Stage: <span className="font-mono bg-purple-100 dark:bg-purple-800 px-1 rounded">{templateStream.stage || 'processing'}</span>
                </div>
              </div>
              {templateStream.progress !== undefined && (
                <div className="text-right">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{templateStream.progress}%</div>
                  <div className="text-xs text-purple-500 dark:text-purple-400">progress</div>
                </div>
              )}
            </div>
            
            {/* Progress bar with percentage */}
            <div className="space-y-1">
              <div className="h-3 bg-purple-200 dark:bg-purple-800 rounded-full overflow-hidden shadow-inner">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 via-indigo-600 to-purple-500 transition-all duration-500 relative"
                  style={{
                    width: `${templateStream.progress || 
                      (templateStream.stage === 'complete' ? 100 : 
                       templateStream.stage === 'template_generation' || templateStream.stage === 'generating' ? 60 : 
                       templateStream.stage === 'structure_extraction' ? 30 : 
                       templateStream.stage === 'parsing' ? 75 : 
                       templateStream.stage === 'retrieval' ? 50 : 10)}%`,
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
                </div>
              </div>
              
              {/* Stage indicators */}
              <div className="flex items-center justify-between text-[10px] text-purple-600 dark:text-purple-400 font-medium pt-1">
                <span className={templateStream.stage === 'started' || templateStream.stage === 'task_started' ? 'text-purple-900 dark:text-purple-200 font-bold' : ''}>
                  ▸ Start
                </span>
                <span className={templateStream.stage === 'structure_extraction' ? 'text-purple-900 dark:text-purple-200 font-bold' : ''}>
                  ▸ Extract
                </span>
                <span className={templateStream.stage === 'template_generation' || templateStream.stage === 'generating' ? 'text-purple-900 dark:text-purple-200 font-bold' : ''}>
                  ▸ Generate
                </span>
                <span className={templateStream.stage === 'parsing' ? 'text-purple-900 dark:text-purple-200 font-bold' : ''}>
                  ▸ Parse
                </span>
                <span className={templateStream.stage === 'complete' ? 'text-green-900 dark:text-green-200 font-bold' : ''}>
                  ✓ Done
                </span>
              </div>
            </div>

            {/* Detailed steps */}
            <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <CheckCircle className={`h-3 w-3 ${templateStream.progress && templateStream.progress > 10 ? 'text-green-500' : 'text-gray-300'}`} />
                  <span className="text-purple-700 dark:text-purple-300">Structure extraction</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className={`h-3 w-3 ${templateStream.progress && templateStream.progress > 30 ? 'text-green-500' : 'text-gray-300'}`} />
                  <span className="text-purple-700 dark:text-purple-300">RAG query</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className={`h-3 w-3 ${templateStream.progress && templateStream.progress > 60 ? 'text-green-500' : 'text-gray-300'}`} />
                  <span className="text-purple-700 dark:text-purple-300">Question generation</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className={`h-3 w-3 ${templateStream.progress && templateStream.progress >= 100 ? 'text-green-500' : 'text-gray-300'}`} />
                  <span className="text-purple-700 dark:text-purple-300">Template complete</span>
                </div>
              </div>
            </div>

            {/* Cancel button - only show if stream can be cancelled */}
            {templateStream.stage !== 'complete' && templateStream.stage !== 'error' && (
              <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-700 flex justify-end">
                <Button
                  onClick={() => {
                    console.log('[RFPUploadModal] User requested to cancel template generation stream');
                    // TODO: Implement stream cancellation
                    alert('Stream cancellation will be available soon. For now, please wait for completion or refresh the page.');
                  }}
                  variant="outline"
                  size="sm"
                  className="text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700 hover:bg-purple-100 dark:hover:bg-purple-800"
                >
                  <StopCircle className="h-3 w-3 mr-1" />
                  Cancel Generation
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Job Processing Progress */}
        {rfpJobStream.isStreaming && uploadedJobId && !isGenerating && (
          <div className="p-4 bg-gradient-to-r from-blue-100 via-indigo-100 to-blue-100 dark:from-blue-900/30 dark:via-indigo-900/30 dark:to-blue-900/30 rounded-lg border-2 border-blue-300 dark:border-blue-700 shadow-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="relative">
                {rfpJobStream.stage === 'parsing' ? (
                  <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-pulse" />
                ) : rfpJobStream.stage === 'chunking' ? (
                  <Search className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-bounce" />
                ) : rfpJobStream.stage === 'indexing' ? (
                  <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                ) : rfpJobStream.stage === 'extracting' ? (
                  <Brain className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-pulse" />
                ) : (
                  <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                )}
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                </span>
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                  {rfpJobStream.message || 'Processing RFP document...'}
                </div>
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  {rfpJobStream.jobType === 'ingest' ? 'Ingesting document' : 
                   rfpJobStream.jobType === 'rfp_structure_extract' ? 'Extracting structure' : 
                   'Processing'}
                </div>
              </div>
              {rfpJobStream.elapsed > 0 && (
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{rfpJobStream.elapsed.toFixed(1)}s</div>
                  <div className="text-xs text-blue-500 dark:text-blue-400">elapsed</div>
                </div>
              )}
              {rfpJobStream.isStreaming && (
                <button
                  onClick={() => rfpJobStream.cancelStream()}
                  className="ml-2 flex items-center gap-1 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  <StopCircle className="h-4 w-4" />
                  Cancel
                </button>
              )}
            </div>
            
            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-blue-700 dark:text-blue-300">
                <span className={rfpJobStream.stage === 'parsing' ? 'text-blue-900 dark:text-blue-200 font-bold' : ''}>
                  Parse
                </span>
                <span className={rfpJobStream.stage === 'chunking' ? 'text-blue-900 dark:text-blue-200 font-bold' : ''}>
                  Chunk
                </span>
                <span className={rfpJobStream.stage === 'indexing' ? 'text-blue-900 dark:text-blue-200 font-bold' : ''}>
                  Index
                </span>
                <span className={rfpJobStream.stage === 'extracting' ? 'text-blue-900 dark:text-blue-200 font-bold' : ''}>
                  Extract
                </span>
                <span className={rfpJobStream.stage === 'complete' ? 'text-blue-900 dark:text-blue-200 font-bold' : ''}>
                  Done
                </span>
              </div>
              <div className="h-2 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden shadow-inner">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300 relative"
                  style={{
                    width: `${rfpJobStream.progress}%`,
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
                </div>
              </div>
            </div>
            
            {isGeneratingTemplate ? (
              <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                <div className="flex items-center gap-2 mb-2">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">Generating Proposal Template</span>
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                  Analyzing RFP documents and creating a customized template...
                </p>
                <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1 list-disc list-inside">
                  <li>Extracting RFP structure and requirements</li>
                  <li>Analyzing document sections and evaluation criteria</li>
                  <li>Generating comprehensive proposal questions</li>
                  <li>Validating question quality</li>
                </ul>
              </div>
            ) : rfpJobStream.stage === 'complete' ? (
              <div className="mt-3 flex items-center gap-2 text-xs text-green-700 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-md">
                <CheckCircle className="h-3 w-3" />
                <span className="font-medium">Processing complete! Generating template...</span>
              </div>
            ) : rfpJobStream.stage === 'error' ? (
              <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-md">
                <AlertCircle className="h-3 w-3" />
                <span className="font-medium">{rfpJobStream.error || 'Processing failed'}</span>
              </div>
            ) : null}
          </div>
        )}


        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          {isGenerating ? (
            // Template generation in progress - disable buttons
            // Progress tracking is shown in ProposalBuilder, no need for redundant UI here
            <Button variant="outline" disabled className="w-full">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Generating Template...
            </Button>
          ) : showConfirmation && rfpJobStream.stage === 'complete' ? (
            // Confirmation step - job completed, ready to generate template
            <>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowConfirmation(false);
                  setUploadedDocumentsInfo([]);
                  setAllArtifactsForTemplate([]);
                  setUploadedArtifactIds([]);
                  setUploadedJobId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  // User explicitly clicked "Generate Template" - this is the ONLY trigger
                  console.log('[RFPUploadModal] User clicked Generate Template button - initiating template generation');
                  // Use all artifacts (uploaded + existing) for template generation
                  const artifactsToUse = allArtifactsForTemplate.length > 0 ? allArtifactsForTemplate : uploadedDocumentsInfo;
                  const allArtifactIds = artifactsToUse.map(doc => doc.artifact_id);
                  console.log('[RFPUploadModal] Passing artifact IDs to template generation:', {
                    artifactIds: allArtifactIds,
                    artifactsCount: artifactsToUse.length,
                    artifacts: artifactsToUse.map(d => ({ id: d.artifact_id, filename: d.filename }))
                  });
                  // Pass the stored artifact IDs and job ID to parent to start template generation
                  // CRITICAL: Use the artifact IDs from the displayed artifacts
                  const finalArtifactIds = allArtifactIds.length > 0 ? allArtifactIds : uploadedArtifactIds;
                  console.log('[RFPUploadModal] Final artifact IDs for template generation:', {
                    finalArtifactIds,
                    allArtifactIds,
                    uploadedArtifactIds,
                    allArtifactsCount: allArtifactsForTemplate.length,
                    uploadedDocsCount: uploadedDocumentsInfo.length
                  });
                  if (finalArtifactIds.length === 0) {
                    console.error('[RFPUploadModal] No artifact IDs available for template generation!', {
                      allArtifactsForTemplate: allArtifactsForTemplate.length,
                      uploadedDocumentsInfo: uploadedDocumentsInfo.length,
                      uploadedArtifactIds: uploadedArtifactIds.length,
                      allArtifacts: allArtifactsForTemplate,
                      uploadedDocs: uploadedDocumentsInfo,
                      uploadedIds: uploadedArtifactIds
                    });
                    alert('No documents available for template generation. Please upload documents first.');
                    return;
                  }
                  console.log('[RFPUploadModal] Calling onUploadComplete with:', {
                    artifactIds: finalArtifactIds,
                    jobId: uploadedJobId || '',
                    count: finalArtifactIds.length
                  });
                  onUploadComplete(finalArtifactIds, uploadedJobId || '');
                }}
                variant="primary"
                disabled={isGenerating || (allArtifactsForTemplate.length === 0 && uploadedDocumentsInfo.length === 0 && uploadedArtifactIds.length === 0)}
              >
                {isGenerating ? 'Generating...' : 'Generate Template'}
              </Button>
            </>
          ) : showConfirmation && (allArtifactsForTemplate.length > 0 || uploadedDocumentsInfo.length > 0) && !rfpJobStream.isStreaming ? (
            // Confirmation step with existing documents (no new upload)
            <>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowConfirmation(false);
                  setAllArtifactsForTemplate([]);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  // User explicitly clicked "Generate Template" - this is the ONLY trigger
                  console.log('[RFPUploadModal] User clicked Generate Template button with existing documents');
                  // Use all artifacts for template generation
                  const artifactsToUse = allArtifactsForTemplate.length > 0 ? allArtifactsForTemplate : uploadedDocumentsInfo;
                  const allArtifactIds = artifactsToUse.map(doc => doc.artifact_id);
                  console.log('[RFPUploadModal] Passing artifact IDs to template generation (existing docs):', {
                    artifactIds: allArtifactIds,
                    artifactsCount: artifactsToUse.length,
                    artifacts: artifactsToUse.map(d => ({ id: d.artifact_id, filename: d.filename }))
                  });
                  const finalArtifactIds = allArtifactIds.length > 0 ? allArtifactIds : uploadedArtifactIds;
                  console.log('[RFPUploadModal] Final artifact IDs for template generation (existing docs):', {
                    finalArtifactIds,
                    allArtifactIds,
                    uploadedArtifactIds
                  });
                  if (finalArtifactIds.length === 0) {
                    console.error('[RFPUploadModal] No artifact IDs available for template generation!');
                    alert('No documents available for template generation. Please upload documents first.');
                    return;
                  }
                  onUploadComplete(finalArtifactIds, '');
                }}
                variant="primary"
                disabled={isGenerating || (allArtifactsForTemplate.length === 0 && uploadedDocumentsInfo.length === 0 && uploadedArtifactIds.length === 0)}
              >
                {isGenerating ? 'Generating...' : 'Generate Template'}
              </Button>
            </>
          ) : showConfirmation && rfpJobStream.isStreaming ? (
            // Confirmation step - job still processing
            <>
              <Button 
                variant="outline" 
                onClick={() => {
                  rfpJobStream.cancelStream();
                  setShowConfirmation(false);
                  setUploadedDocumentsInfo([]);
                  setAllArtifactsForTemplate([]);
                  setUploadedArtifactIds([]);
                  setUploadedJobId(null);
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" disabled>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing Documents...
              </Button>
            </>
          ) : rfpJobStream.isStreaming ? (
            // Job is processing - show cancel button
            <Button 
              variant="outline" 
              onClick={() => {
                rfpJobStream.cancelStream();
                onClose();
              }}
            >
              Cancel Processing
            </Button>
          ) : showConfirmation && (uploadedDocumentsInfo.length > 0 || allArtifactsForTemplate.length > 0) ? (
            // Confirmation step with artifacts - show Generate Template button
            <>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowConfirmation(false);
                  setUploadedDocumentsInfo([]);
                  setAllArtifactsForTemplate([]);
                  setUploadedArtifactIds([]);
                  setUploadedJobId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const artifactsToUse = allArtifactsForTemplate.length > 0 ? allArtifactsForTemplate : uploadedDocumentsInfo;
                  const allArtifactIds = artifactsToUse.map(doc => doc.artifact_id);
                  console.log('[RFPUploadModal] Passing artifact IDs to template generation (fallback):', {
                    artifactIds: allArtifactIds,
                    artifactsCount: artifactsToUse.length,
                    artifacts: artifactsToUse.map(d => ({ id: d.artifact_id, filename: d.filename }))
                  });
                  const finalArtifactIds = allArtifactIds.length > 0 ? allArtifactIds : uploadedArtifactIds;
                  console.log('[RFPUploadModal] Final artifact IDs for template generation (with artifacts):', {
                    finalArtifactIds,
                    allArtifactIds,
                    uploadedArtifactIds
                  });
                  if (finalArtifactIds.length === 0) {
                    console.error('[RFPUploadModal] No artifact IDs available for template generation!');
                    alert('No documents available for template generation. Please upload documents first.');
                    return;
                  }
                  onUploadComplete(finalArtifactIds, uploadedJobId || '');
                }}
                variant="primary"
                disabled={isGeneratingTemplate || (allArtifactsForTemplate.length === 0 && uploadedDocumentsInfo.length === 0 && uploadedArtifactIds.length === 0)}
              >
                {isGenerating ? 'Generating...' : 'Generate Template'}
              </Button>
            </>
          ) : (
            // No job running - show upload button
            <>
              <Button variant="outline" onClick={onClose} disabled={uploading}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={files.length === 0 || uploading}
                isLoading={uploading}
              >
                {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * Format file size in bytes to human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
